import { PublishedQuoteListItemDto } from "@lib/ShiOneClient";
import { Theme } from "@mui/material";
import { createSavedCurrencyFormatter, isKnownCurrency } from "shared-ui/src/functions/currency";
import { formatCurrency as sharedFormatCurrency } from "shared-ui/src/components/ecommerce/helpers/EcommerceCurrencyFormatHelper";

export interface ExpirationInfo {
  text: string;
  color: string;
  isExpired: boolean;
}


/**
 * Format the secondary text parts for a quote (ID, contact, item count, total value)
 */
export function formatQuoteSecondaryText(
  quote: PublishedQuoteListItemDto,
  isTabularView?: boolean
): string[] {
  const parts = [];

  if (quote.quoteId) {
    parts.push(`Quote #${quote.quoteId}`);
  }

  // Add "Requested by" in tabular view
  if (isTabularView && quote.contact) {
    parts.push(`Requested by ${quote.contact}`);
  }

  if (quote.itemCount !== undefined && quote.itemCount !== null) {
    parts.push(`${quote.itemCount} ${quote.itemCount === 1 ? 'product' : 'products'}`);
  }

  if (quote.totalValue !== undefined && quote.totalValue !== null) {
    // The summary feed can leave currencyId at 0, or send an id the shared reference lacks, with only a
    // culture-formatted currencyFormat.
    parts.push(
      isKnownCurrency(quote.currencyId)
        ? createSavedCurrencyFormatter(quote.currencyId).format(quote.totalValue)
        : sharedFormatCurrency(quote.totalValue, quote.currencyFormat),
    );
  }

  return parts;
}

/**
 * Get expiration information for a quote including display text, color, and expired status
 */
export function getQuoteExpirationInfo(
  expirationDate: Date | undefined,
  theme: Theme
): ExpirationInfo | null {
  if (!expirationDate) {
    return null;
  }

  const now = new Date();
  const diffTime = expirationDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    // Expired (past date)
    return {
      text: "Expired",
      color: theme.palette.error.main,
      isExpired: true,
    };
  } else if (diffDays <= 30) {
    // Expires within 30 days - Orange
    return {
      text: `Expires in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`,
      color: theme.palette.warning.main,
      isExpired: false,
    };
  } else {
    // Expires more than 30 days from now - Green
    const formattedDate = expirationDate.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
    return {
      text: `Expires on ${formattedDate}`,
      color: theme.palette.success.main,
      isExpired: false,
    };
  }
}

/**
 * Filter and sort quotes based on permissions and selected tab
 * Note: Assumes deleted quotes have already been filtered out
 */
export function filterAndSortQuotes(
  quotes: PublishedQuoteListItemDto[] | undefined,
  hasOrgUnitPermission: boolean,
  selectedTab: number,
  maxResults: number = 5
): PublishedQuoteListItemDto[] {
  if (!quotes || quotes.length === 0) {
    return [];
  }

  let filtered: PublishedQuoteListItemDto[];

  if (!hasOrgUnitPermission) {
    // User only has quote-view-my permission, quotes filtered on the backend.
    filtered = quotes;
  } else {
    // User has quote-view-org-unit permission, filter by tab
    switch (selectedTab) {
      case 0: // All Quotes
        filtered = quotes;
        break;
      case 1: // My Quotes
        filtered = quotes.filter((quote) => quote.isMyQuote);
        break;
      case 2: // Storefront Quotes
        filtered = quotes.filter((quote) => !quote.isMyQuote);
        break;
      default:
        filtered = quotes;
    }
  }

  // Sort by createdOn (most recent first) and limit to maxResults
  return filtered
    .sort((a, b) => {
      const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
      const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    })
    .slice(0, maxResults);
}
